import { useCallback, useEffect, useRef } from 'react';
import { logActivity } from '../lib/api';
import type { Progress } from '../lib/types';

// ─────────────────────────────────────────────────────────────────────────────
// Time on an assignment, as the teacher will see it.
//
// A second counts only while the tab is visible AND the student has done
// something in the last `idleMs` (moved, scrolled, typed, tapped) — or a video
// is playing. A tab left open over lunch is not an hour of reading.
//
// Batches are flushed every FLUSH_MS and on leaving the page. The server clamps
// each batch to 120 seconds, so FLUSH_MS must stay well under that.
//
// The page reads `pendingSeconds()` / `pendingVideo()` to show a live pass
// tally: server's pass_seconds + what has not been sent yet.
//
// With `manualSeconds`, this hook counts no time itself: the page calls
// addSeconds() for the seconds it judges real (a story credits only seconds of
// actual reading, so scrolling up and down earns nothing).
// ─────────────────────────────────────────────────────────────────────────────

const FLUSH_MS = 15_000;
const DEFAULT_IDLE_MS = 90_000;

type Pending = { seconds: number; lookups: number; videoWatched: number; videoDuration: number | null };

export function useActivityLog(
  assignmentId: string | undefined,
  enabled: boolean,
  onSaved: (p: Progress) => void,
  idleMs: number = DEFAULT_IDLE_MS,
  manualSeconds = false,
) {
  const pending = useRef<Pending>({ seconds: 0, lookups: 0, videoWatched: 0, videoDuration: null });
  const lastInput = useRef(Date.now());
  const playing = useRef(false);
  const savedRef = useRef(onSaved);
  savedRef.current = onSaved;

  const isActive = useCallback(
    () => document.visibilityState === 'visible'
      && (playing.current || Date.now() - lastInput.current < idleMs),
    [idleMs],
  );

  /**
   * Send what is pending. With `complete`, also ask the server to count a pass;
   * a refusal (too quick, not enough played) throws with the server's message
   * and the pending time is kept for the next try.
   */
  const flush = useCallback(async (complete = false, score?: { correct: number; total: number }) => {
    if (!assignmentId) return;
    const p = pending.current;
    if (!complete && p.seconds < 1 && p.lookups === 0 && p.videoWatched < 1) return;
    pending.current = { seconds: 0, lookups: 0, videoWatched: 0, videoDuration: p.videoDuration };
    try {
      const saved = await logActivity(assignmentId, { ...p, complete, ...score });
      savedRef.current(saved);
    } catch (e) {
      pending.current.seconds += p.seconds;
      pending.current.lookups += p.lookups;
      pending.current.videoWatched += p.videoWatched;
      if (complete) throw e;
      console.error('[activity] flush failed', e);
    }
  }, [assignmentId]);

  // Counting runs for everyone — the teacher's preview shows the same live
  // checklist — but only a student's time is ever SENT (the effect below).
  useEffect(() => {
    if (!assignmentId) return;
    const bump = () => { lastInput.current = Date.now(); };
    const events = ['pointermove', 'pointerdown', 'keydown', 'scroll', 'wheel', 'touchstart'] as const;
    events.forEach(e => window.addEventListener(e, bump, { passive: true }));
    const tick = window.setInterval(() => {
      if (!manualSeconds && isActive()) pending.current.seconds += 1;
    }, 1000);
    return () => {
      events.forEach(e => window.removeEventListener(e, bump));
      window.clearInterval(tick);
    };
  }, [assignmentId, isActive, manualSeconds]);

  useEffect(() => {
    if (!enabled || !assignmentId) return;

    const flusher = window.setInterval(() => { void flush(); }, FLUSH_MS);
    const onHide = () => { if (document.visibilityState === 'hidden') void flush(); };
    document.addEventListener('visibilitychange', onHide);
    window.addEventListener('pagehide', onHide);

    // Opening the page is itself recorded, so the teacher sees "opened" even
    // for a visit too short to accumulate a flush.
    void logActivity(assignmentId, {}).then(p => savedRef.current(p)).catch(() => {});

    return () => {
      window.clearInterval(flusher);
      document.removeEventListener('visibilitychange', onHide);
      window.removeEventListener('pagehide', onHide);
      void flush();
    };
  }, [enabled, assignmentId, flush]);

  return {
    addLookup: useCallback(() => { pending.current.lookups += 1; }, []),
    addSeconds: useCallback((s: number) => { pending.current.seconds += s; }, []),
    addVideoSeconds: useCallback((s: number, duration: number | null) => {
      pending.current.videoWatched += s;
      if (duration) pending.current.videoDuration = duration;
    }, []),
    setPlaying: useCallback((p: boolean) => { playing.current = p; }, []),
    isActive,
    pendingSeconds: useCallback(() => pending.current.seconds, []),
    pendingVideo: useCallback(() => pending.current.videoWatched, []),
    complete: useCallback((score?: { correct: number; total: number }) => flush(true, score), [flush]),
  };
}
